import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { Note } from '@chatapp-inbox/shared';
import { createNote, deleteNote, isApiError, listNotes, updateNote } from '../../api';
import { apiErrorMessage } from './error-message';
import { conversationKey, invalidateConversationLists, notesKey } from '../conversation';
import { noteDraftKey } from './use-note-draft';

/** Notas da conversa, da mais antiga para a mais nova (a ordem da API: o cache não é invertido). */
export function useNotes(conversationId: string) {
  return useQuery({
    queryKey: notesKey(conversationId),
    queryFn: ({ signal }) => listNotes(conversationId, signal),
  });
}

/**
 * Mensagem para a pessoa quando uma ação de nota falha. O servidor já responde em português
 * ("A nota está vazia", "Máximo de 5000 caracteres"…), então a mensagem dele vale; o que o texto
 * cru não diz é o que fazer quando a conversa deixou de ser sua.
 */
export function noteErrorMessage(error: unknown, fallback: string): string {
  if (isApiError(error) && error.code === 'not_assigned') {
    return 'Você não é mais o responsável por esta conversa. Assuma-a de novo para adicionar notas.';
  }
  return apiErrorMessage(error, fallback);
}

const isNotFound = (error: unknown): boolean => isApiError(error) && error.status === 404;

/**
 * Atualiza a lista de notas em cache com a resposta do servidor, sem refazer a busca. Sem lista em
 * cache (ainda carregando, ou a busca falhou) não há o que remendar: pede a lista de novo, que já
 * virá com a mudança.
 */
function patchNotes(
  queryClient: QueryClient,
  conversationId: string,
  change: (notes: Note[]) => Note[],
): void {
  const key = notesKey(conversationId);
  if (queryClient.getQueryData<Note[]>(key) === undefined) {
    void queryClient.invalidateQueries({ queryKey: key });
    return;
  }
  queryClient.setQueryData<Note[]>(key, (notes) => (notes ? change(notes) : notes));
}

/**
 * Cria a nota. Os efeitos no cache ficam nas opções do hook (e não em `mutate(…, { onSuccess })`)
 * porque valem mesmo que o formulário já tenha sido desmontado (a pessoa trocou de conversa).
 */
export function useCreateNote(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (text: string) => createNote(conversationId, text),
    onSuccess: (note, text) => {
      // A lista já pode ter a nota (o tempo real da T26 pode chegar antes da resposta).
      patchNotes(queryClient, conversationId, (notes) =>
        notes.some((n) => n.id === note.id) ? notes : [...notes, note],
      );
      // O rascunho só some se ainda for o texto enviado (o campo fica travado durante o envio).
      const draft = queryClient.getQueryData<string>(noteDraftKey(conversationId));
      if (draft !== undefined && draft.trim() === text) {
        queryClient.setQueryData(noteDraftKey(conversationId), '');
      }
    },
    onError: (error) => {
      // 403 not_assigned: a conversa mudou de dono enquanto a pessoa digitava. Refaz a conversa
      // para a tela deixar de oferecer o formulário (o rascunho continua guardado).
      if (isApiError(error) && error.code === 'not_assigned') {
        void queryClient.invalidateQueries({ queryKey: conversationKey(conversationId) });
        void invalidateConversationLists(queryClient);
      }
    },
  });
}

/** Edita o texto de uma nota (só o autor). */
export function useUpdateNote(conversationId: string, noteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (text: string) => updateNote(conversationId, noteId, text),
    onSuccess: (note) => {
      patchNotes(queryClient, conversationId, (notes) =>
        notes.map((n) => (n.id === note.id ? note : n)),
      );
    },
    onError: (error) => {
      // A nota sumiu (apagada em outro lugar): a lista em cache está velha.
      if (isNotFound(error)) {
        void queryClient.invalidateQueries({ queryKey: notesKey(conversationId) });
      }
    },
  });
}

/**
 * Apaga uma nota (definitivo). "Não encontrada" conta como sucesso: o que a pessoa quer é que a
 * nota não exista mais, e alguém (ou ela mesma, em outra aba) já apagou.
 */
export function useDeleteNote(conversationId: string, noteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      try {
        await deleteNote(conversationId, noteId);
      } catch (error) {
        if (!isNotFound(error)) throw error;
      }
    },
    onSuccess: () => {
      patchNotes(queryClient, conversationId, (notes) => notes.filter((n) => n.id !== noteId));
    },
  });
}
